import { isArray, IsInterface, isNullable, isSingletonStringUnion, isString } from "generic-type-guard";

import { RestRequestPayload } from "@com.mgmtp.a12.utils/utils-connector/lib/main";

import { isRecord } from "../../utilities/typeGuardUtils";

const isMetadataOption = new IsInterface()
	.withProperties({
		value: isString,
		label: isString
	})
	.get();

export type XakteMetadataOptions = {
	options: Record<string, Array<{ value: string; label: string }>>;
	defaults: Record<string, unknown>;
};

export function isXakteMetadataOptions(value: unknown): value is XakteMetadataOptions {
	if (!isRecord(value) || !isRecord(value.options) || !isRecord(value.defaults)) {
		return false;
	}
	return Object.values(value.options).every(isArray(isMetadataOption));
}

const isValidationMessage = new IsInterface()
	.withProperties({
		code: isString,
		message: isString
	})
	.get();

export const isXakteArchivePreparation = new IsInterface()
	.withProperties({
		preparationId: isString,
		status: isSingletonStringUnion("PENDING", "SUCCEEDED", "FAILED"),
		previewId: isNullable(isString),
		fileName: isNullable(isString),
		warnings: isArray(isValidationMessage)
	})
	.withOptionalProperties({
		errorMessage: isNullable(isString)
	})
	.get();
export type XakteArchivePreparation = {
	preparationId: string;
	status: "PENDING" | "SUCCEEDED" | "FAILED";
	previewId: string | null;
	fileName: string | null;
	warnings: Array<{ code: string; message: string }>;
	errorMessage?: string | null;
};

export const isXakteArchiveResult = new IsInterface()
	.withProperties({
		previewId: isString,
		status: isSingletonStringUnion("PENDING", "SUCCEEDED", "FAILED")
	})
	.withOptionalProperties({
		jobId: isNullable(isString),
		externalStatus: isNullable(isString),
		errorCode: isNullable(isString),
		errorMessage: isNullable(isString)
	})
	.get();
export type XakteArchiveResult = {
	previewId: string;
	status: "PENDING" | "SUCCEEDED" | "FAILED";
	jobId?: string | null;
	externalStatus?: string | null;
	errorCode?: string | null;
	errorMessage?: string | null;
};

export type XakteArchiveMetadata = {
	archiveFile: {
		subject?: string | null;
		reference?: string | null;
		archiveCase: {
			subject?: string | null;
			reference?: string | null;
			primaryDocument: {
				subject?: string | null;
				reference?: string | null;
				documentType?: string | null;
			};
		};
	};
};

export type XakteArchiveDomainData = {
	absender: string | null;
	mappenNummer: string | null;
	papierform: string | null;
};

export function xakteMetadataOptionsRequest(documentReference: string): RestRequestPayload {
	return {
		relativeUrl: `/xakte/metadataOptions?documentReference=${documentReference}`,
		method: "GET"
	};
}

export function xaktePreviewRequest(
	documentReference: string,
	attachmentIds: string[],
	metadata: XakteArchiveMetadata,
	archiveDomainData: XakteArchiveDomainData,
	archiveLocation: string
): RestRequestPayload {
	return {
		relativeUrl: "/xakte/preview",
		method: "POST",
		body: JSON.stringify({
			documentReference,
			archiveConfigs: {
				useDocConversion: false,
				metadata,
				archiveDomainData,
				attachmentIds
			},
			archiveLocation
		})
	};
}

export function xakteSubmitRequest(previewId: string): RestRequestPayload {
	return {
		relativeUrl: "/xakte/archive",
		method: "POST",
		body: JSON.stringify({ previewId })
	};
}

export function xaktePreparationStatusRequest(preparationId: string): RestRequestPayload {
	return {
		relativeUrl: `/xakte/preview/${preparationId}`,
		method: "GET"
	};
}

export function xaktePreviewDownloadRequest(previewId: string): RestRequestPayload {
	return {
		relativeUrl: `/xakte/preview/${previewId}/download`,
		method: "GET",
		customHeaders: [["Accept", "application/x-zip-compressed"]]
	};
}

export function xakteStatusRequest(previewId: string): RestRequestPayload {
	return {
		relativeUrl: `/xakte/archive/${encodeURIComponent(previewId)}`,
		method: "GET"
	};
}
